import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Modal from "newui/components/ui/Modal/Modal";

const MAX_SCORE = 10;

interface SoloScoreEditValues {
  new_score: string;
  edit_reason: string;
}

interface SoloScoreEditModalProps {
  isOpen: boolean;
  supplierName: string;
  criteriaName: string;
  criteriaId: number;
  inviteId: number;
  currentScore: number | null;
  maxScore?: number;
  isSaving: boolean;
  onClose: () => void;
  onSave: (criteriaId: number, inviteId: number, newScore: number, editReason: string) => void;
}

const buildValidationSchema = (maxScore: number) =>
  Yup.object({
    new_score: Yup.number()
      .required("Score is required")
      .min(0, "Score must be at least 0")
      .max(maxScore, `Score must be at most ${maxScore}`)
      .typeError("Score must be a number"),
    edit_reason: Yup.string()
      .trim()
      .required("Edit reason is required")
      .min(1, "Edit reason is required"),
  });

export const SoloScoreEditModal: React.FC<SoloScoreEditModalProps> = ({
  isOpen,
  supplierName,
  criteriaName,
  criteriaId,
  inviteId,
  currentScore,
  maxScore = MAX_SCORE,
  isSaving,
  onClose,
  onSave,
}) => {
  const validationSchema = buildValidationSchema(maxScore);

  const initialValues: SoloScoreEditValues = {
    new_score: currentScore !== null ? String(currentScore) : "",
    edit_reason: "",
  };

  const handleSubmit = (values: SoloScoreEditValues) => {
    onSave(criteriaId, inviteId, Number(values.new_score), values.edit_reason.trim());
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur
      validateOnChange={false}
      enableReinitialize
    >
      {({ errors, touched, isValid, dirty, values, handleChange, handleBlur, submitForm }) => (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          title="Edit Score"
          size="md"
          iconType="edit"
          modalType="standard"
          closeOnEsc={!isSaving}
          closeOnOverlayClick={false}
          primaryButton={{
            text: isSaving ? "Saving..." : "Save",
            onClick: submitForm,
            disabled: !isValid || !dirty || isSaving,
            isLoading: isSaving,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: onClose,
            disabled: isSaving,
          }}
        >
          <Form>
            <div className="space-y-4">
              <div className="rounded-md bg-gray-50 border border-gray-200 px-4 py-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-500">Supplier</span>
                  <span className="font-medium text-gray-900">{supplierName}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <span className="text-gray-500">Criterion</span>
                  <span className="font-medium text-gray-900">{criteriaName}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <span className="text-gray-500">Current Score</span>
                  <span
                    className="font-semibold tabular-nums text-gray-900"
                    data-testid="current-score-display"
                  >
                    {currentScore !== null ? currentScore : "—"}
                  </span>
                </div>
              </div>

              <div>
                <label
                  htmlFor="new_score"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  New Score
                  <span className="text-gray-400 font-normal ml-1">
                    (0 – {maxScore})
                  </span>
                </label>
                <Field
                  as="input"
                  type="number"
                  id="new_score"
                  name="new_score"
                  min={0}
                  max={maxScore}
                  step="0.1"
                  value={values.new_score}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  data-testid="new-score-input"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  aria-describedby={errors.new_score && touched.new_score ? "new_score_error" : undefined}
                />
                {errors.new_score && touched.new_score && (
                  <p
                    id="new_score_error"
                    className="mt-1 text-xs text-red-600"
                    role="alert"
                  >
                    {errors.new_score}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="edit_reason"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Reason for Edit
                  <span className="text-red-500 ml-1" aria-hidden="true">*</span>
                </label>
                <textarea
                  id="edit_reason"
                  name="edit_reason"
                  value={values.edit_reason}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={3}
                  placeholder="Explain why this score is being changed..."
                  data-testid="edit-reason-textarea"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                  aria-required="true"
                  aria-describedby={errors.edit_reason && touched.edit_reason ? "edit_reason_error" : undefined}
                />
                {errors.edit_reason && touched.edit_reason && (
                  <p
                    id="edit_reason_error"
                    className="mt-1 text-xs text-red-600"
                    role="alert"
                  >
                    {errors.edit_reason}
                  </p>
                )}
              </div>
            </div>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
